const DPOP_ALGORITHM = { name: "ECDSA", namedCurve: "P-256" };
const DPOP_SIGN_ALGORITHM = { name: "ECDSA", hash: "SHA-256" };

let dpopKeyPairPromise = null;

function getCrypto() {
  if (typeof crypto === "undefined" || !crypto.subtle) {
    throw new Error("Web Crypto is required to create DPoP proofs.");
  }
  return crypto;
}

function getTextEncoder() {
  if (typeof TextEncoder === "undefined") {
    throw new Error("TextEncoder is required to create DPoP proofs.");
  }
  return new TextEncoder();
}

function base64Url(bytes) {
  const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join("");
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function base64UrlJson(value) {
  return base64Url(getTextEncoder().encode(JSON.stringify(value)));
}

function normalizeProofUrl(url) {
  if (!url) throw new Error("DPoP proof URL is required.");
  const baseUrl = typeof window !== "undefined" && window.location ? window.location.href : undefined;
  const parsed = baseUrl ? new URL(url, baseUrl) : new URL(url);
  parsed.search = "";
  parsed.hash = "";
  return parsed.toString();
}

function defaultNow() {
  return Math.floor(Date.now() / 1000);
}

function defaultRandomId() {
  const cryptoApi = getCrypto();
  if (typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  return base64Url(bytes);
}

async function sha256Base64Url(value) {
  const hash = await getCrypto().subtle.digest("SHA-256", getTextEncoder().encode(value));
  return base64Url(new Uint8Array(hash));
}

function stripPrivateJwkFields(jwk) {
  const publicJwk = { ...jwk };
  delete publicJwk.d;
  delete publicJwk.dp;
  delete publicJwk.dq;
  delete publicJwk.q;
  delete publicJwk.qi;
  delete publicJwk.key_ops;
  delete publicJwk.ext;
  return publicJwk;
}

function derIntegerToFixedBytes(bytes) {
  let start = 0;
  while (start < bytes.length - 1 && bytes[start] === 0) start += 1;
  const unsignedBytes = bytes.slice(start);
  if (unsignedBytes.length > 32) {
    return unsignedBytes.slice(unsignedBytes.length - 32);
  }
  const output = new Uint8Array(32);
  output.set(unsignedBytes, 32 - unsignedBytes.length);
  return output;
}

function derSignatureToRaw(signature) {
  const bytes = new Uint8Array(signature);
  if (bytes.length === 64) return bytes;
  if (bytes[0] !== 0x30) {
    throw new Error("Unsupported ECDSA signature format.");
  }

  let offset = 2;
  if (bytes[1] & 0x80) {
    offset = 2 + (bytes[1] & 0x7f);
  }

  if (bytes[offset] !== 0x02) {
    throw new Error("Invalid ECDSA signature format.");
  }
  const rLength = bytes[offset + 1];
  const r = bytes.slice(offset + 2, offset + 2 + rLength);
  offset += 2 + rLength;

  if (bytes[offset] !== 0x02) {
    throw new Error("Invalid ECDSA signature format.");
  }
  const sLength = bytes[offset + 1];
  const s = bytes.slice(offset + 2, offset + 2 + sLength);

  const raw = new Uint8Array(64);
  raw.set(derIntegerToFixedBytes(r), 0);
  raw.set(derIntegerToFixedBytes(s), 32);
  return raw;
}

export async function getDpopKeyPair() {
  if (!dpopKeyPairPromise) {
    dpopKeyPairPromise = getCrypto().subtle.generateKey(DPOP_ALGORITHM, true, ["sign", "verify"]);
  }
  return dpopKeyPairPromise;
}

export async function createDpopProof({
  url,
  method,
  accessToken,
  nowSeconds = defaultNow,
  randomId = defaultRandomId
} = {}) {
  const { publicKey, privateKey } = await getDpopKeyPair();
  const publicJwk = stripPrivateJwkFields(await getCrypto().subtle.exportKey("jwk", publicKey));
  const payload = {
    jti: randomId(),
    htm: String(method || "GET").toUpperCase(),
    htu: normalizeProofUrl(url),
    iat: nowSeconds()
  };

  if (accessToken) {
    payload.ath = await sha256Base64Url(String(accessToken));
  }

  const header = {
    typ: "dpop+jwt",
    alg: "ES256",
    jwk: publicJwk
  };
  const signingInput = `${base64UrlJson(header)}.${base64UrlJson(payload)}`;
  const signature = await getCrypto().subtle.sign(
    DPOP_SIGN_ALGORITHM,
    privateKey,
    getTextEncoder().encode(signingInput)
  );

  return `${signingInput}.${base64Url(derSignatureToRaw(signature))}`;
}
